import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CrosslistSubnav, PageIntro } from "@/components/SellerToolsNav";

export default function Labels() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#E11D48");
  const { data } = useQuery({
    queryKey: ["seller-labels"],
    queryFn: async () => (await api.get("/seller/labels")).data,
  });
  const labels = data?.labels || [];
  const colors = data?.colors || [];

  const create = async () => {
    if (!name.trim()) return toast.error("Name the label");
    try {
      await api.post("/seller/labels", { name, color });
      setName("");
      qc.invalidateQueries({ queryKey: ["seller-labels"] });
      toast.success("Label created");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async (row) => {
    try {
      await api.put(`/seller/labels/${row.id}`, { name: row.name, color: row.color });
      qc.invalidateQueries({ queryKey: ["seller-labels"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/seller/labels/${id}`);
      qc.invalidateQueries({ queryKey: ["seller-labels"] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-labels">
      <CrosslistSubnav />
      <PageIntro
        kicker="Crosslist"
        title="Label Manager"
        blurb="Color-coded stickers for source, consignment, and action items. Filter inventory and analytics by label. Up to 50 labels per listing."
      />
      <div className="flex flex-wrap gap-2 mb-8">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New label" className="px-3 py-2 border border-aura-border rounded-lg bg-aura-surface text-sm" data-testid="label-name" />
        <div className="flex gap-1">
          {colors.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} className={`w-6 h-6 rounded-full ${color === c ? "ring-2 ring-white" : ""}`} style={{ background: c }} />
          ))}
        </div>
        <button type="button" onClick={create} className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg" data-testid="label-create">Add New Label</button>
      </div>
      <div className="text-xs text-aura-muted mb-3">TOTAL LABELS: {labels.length}</div>
      {labels.length === 0 ? (
        <div className="py-16 text-center text-aura-muted">Create your first label to get started!</div>
      ) : (
        <div className="space-y-2">
          {labels.map((row) => (
            <div key={row.id} className="flex items-center gap-3 border border-aura-border rounded-lg p-3">
              <span className="w-3 h-3 rounded-full" style={{ background: row.color }} />
              <input defaultValue={row.name} onBlur={(e) => { if (e.target.value !== row.name) save({ ...row, name: e.target.value }); }} className="flex-1 bg-transparent outline-none text-sm" />
              <span className="text-xs font-mono text-aura-muted">{row.count || 0}</span>
              <button type="button" onClick={() => remove(row.id)} className="text-xs text-red-400">Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
